import React, { useMemo } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

// DM panel for the perception prompt flow. One button: "Ask Perception" —
// fires a prompt at every active player. As responses land, they show up
// in a live list with each player's roll and what they discovered.

export default function PerceptionPromptPanel() {
  const { players, perceptionPrompts, resolvePerceptionPrompt } = useCampaign();

  // The most recent unresolved prompt (typical case — DM uses one at a time).
  const activePrompt = useMemo(() => {
    const list = Object.values(perceptionPrompts || {})
      .filter((p) => !p.resolved)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return list[0] || null;
  }, [perceptionPrompts]);

  // Who hasn't responded yet?
  const pending = useMemo(() => {
    if (!activePrompt) return [];
    return (activePrompt.targetPlayerIds || []).filter(
      (pid) => !activePrompt.responses?.[pid]
    );
  }, [activePrompt]);

  const handleDismiss = async () => {
    if (!activePrompt) return;
    await resolvePerceptionPrompt(activePrompt.id);
  };

  // The "Ask Perception" trigger now lives in the DM top bar next to the dice.
  // This panel only surfaces when there's an active prompt, showing the live
  // response list. When idle, render nothing so the sidebar stays uncluttered.
  if (!activePrompt) return null;

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>🕵 Perception</span>
        <button style={styles.dismissBtn} onClick={handleDismiss} title="Close this prompt">✕</button>
      </div>
      <div style={styles.subtitle}>
        {pending.length === 0 ? 'All responses in.' : `Waiting on ${pending.length} player${pending.length === 1 ? '' : 's'}…`}
      </div>
      <div style={styles.list}>
        {(activePrompt.targetPlayerIds || []).map((pid) => {
          const player = players?.[pid];
          const resp   = activePrompt.responses?.[pid];
          const tag    = resp?.isPassive ? 'passive' : 'rolled';
          return (
            <div key={pid} style={styles.row}>
              <span style={styles.name}>{player?.charName || player?.name || pid.slice(0, 6)}</span>
              {resp ? (
                <>
                  <span style={styles.roll}>{tag} <strong>{resp.roll}</strong></span>
                  <span style={styles.discoveries}>
                    {(resp.discoveredDoors?.length || 0) > 0 && `🚪 ${resp.discoveredDoors.length} `}
                  </span>
                </>
              ) : (
                <span style={styles.waiting}>waiting…</span>
              )}
            </div>
          );
        })}
      </div>
      <button style={styles.resolveBtn} onClick={handleDismiss}>Close prompt</button>
    </div>
  );
}

const styles = {
  panel:    { background: 'rgba(122,87,184,0.08)', border: '1px solid rgba(122,87,184,0.3)', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  header:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:    { fontSize: 12, fontFamily: "'Press Start 2P', monospace", color: '#a987d4', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 },
  dismissBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 14, cursor: 'pointer', padding: '0 2px' },
  hint:     { fontSize: 11, color: '#9a8b6e', lineHeight: 1.35 },
  askBtn:   { padding: '10px 14px', background: 'linear-gradient(180deg, #a987d4, #7a57b8)', border: '1px solid #c9b3e8', color: '#1a0e30', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
  subtitle: { fontSize: 11, color: '#9a8b6e', fontStyle: 'italic' },
  list:     { display: 'flex', flexDirection: 'column', gap: 4 },
  row:      { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#0e1528', border: '1px solid #2a3552', borderRadius: 4 },
  name:     { fontSize: 12, fontWeight: 700, color: '#c9b3e8', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  roll:     { fontSize: 11, color: '#f0e6d0', fontVariantNumeric: 'tabular-nums' },
  discoveries:{ fontSize: 11, color: '#86efac' },
  waiting:  { fontSize: 11, color: '#6a5d8a', fontStyle: 'italic' },
  resolveBtn:{ padding: '8px 12px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
};
